import { gql, type TypedDocumentNode } from "@apollo/client";
import type { PaginationVariables } from "@/components/Pagination/Pagination";
import type {
  CommissionFundingBatchLineNode,
  CommissionFundingBatchLinesConnection,
  CommissionFundingBatchNode,
  CommissionFundingBatchStatus
} from "./types";

export interface CommissionFundingBatchesQueryResponse {
  commissionFundingBatches: {
    nodes: CommissionFundingBatchNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
}

export interface CommissionFundingBatchesQueryVariables extends PaginationVariables {
  partnerId?: string;
  status?: CommissionFundingBatchStatus;
}

export const COMMISSION_FUNDING_BATCHES_QUERY: TypedDocumentNode<
  CommissionFundingBatchesQueryResponse,
  CommissionFundingBatchesQueryVariables
> = gql`
  query CommissionFundingBatches(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $partnerId: ID
    $status: BackofficeCommissionFundingBatchStatus
  ) {
    commissionFundingBatches(
      first: $first
      after: $after
      last: $last
      before: $before
      partnerId: $partnerId
      status: $status
    ) {
      nodes {
        id
        status
        invoicePresentation
        totalAmountCents
        lineCount
        issuedOn
        dueOn
        paidOn
        workdayReference
        createdAt
        partner {
          id
          name
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export interface CommissionFundingBatchQueryResponse {
  commissionFundingBatch: CommissionFundingBatchNode & {
    lines: CommissionFundingBatchLineNode[];
    linesConnection: CommissionFundingBatchLinesConnection;
  };
}

export interface CommissionFundingBatchQueryVariables {
  id: string;
  linesFirst?: number;
  linesAfter?: string;
}

export const COMMISSION_FUNDING_BATCH_QUERY: TypedDocumentNode<
  CommissionFundingBatchQueryResponse,
  CommissionFundingBatchQueryVariables
> = gql`
  query CommissionFundingBatch($id: ID!, $linesFirst: Int, $linesAfter: String) {
    commissionFundingBatch(id: $id) {
      id
      status
      invoicePresentation
      totalAmountCents
      lineCount
      issuedOn
      dueOn
      paidOn
      workdayReference
      createdAt
      partner {
        id
        name
      }
      lines {
        id
        pgaId
        personDisplayName
        amountCents
        fundingStatus
      }
      linesConnection(first: $linesFirst, after: $linesAfter) {
        nodes {
          id
          person {
            id
            displayName
            pgaId
          }
          amountCents
          fundingStatus
          payoutEligibilityStatus
          holdReasons
          lineKind
          receivedAt
          payableAt
          paidOutAt
          expiredAt
          facilityName
          fundingBatch {
            id
            status
          }
          releaseBatch {
            id
            status
          }
          splitLines {
            id
          }
          campaign {
            id
            editionYear
            partner {
              id
              name
            }
            program {
              id
              name
            }
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
      }
    }
  }
`;
